import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, TextField,
  DialogActions, Table, TableHead, TableBody, TableRow, TableCell,
  FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, FormControlLabel
} from '@mui/material';

const apiURL = process.env.REACT_APP_API_URL || 'http://localhost:7071';


const RoomManager = () => {
  // PropertyId state and effect moved inside component
  const [propertyId, setPropertyId] = useState(null);
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    const pid = storedUser?.propertyId?.id || storedUser?.properties?.[0]?.propertyId?.id;
    console.log("📌 Refreshed propertyId:", pid);
    setPropertyId(pid);
  }, []);
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingRoomId, setEditingRoomId] = useState(null);
  
  const [formData, setFormData] = useState({
    roomType: '',
    images: [],
    description: '',
    surface: '',
    bedTypes: [],
    doubleBedCount: '',
    singleBedCount: '',
    viewOptions: '',
    requiresBedChoice: false,
    fixedBedSetup: [],
    roomFeatures: [],
    bathroomFeatures: [],
    mediaAndTech: [],
    foodAndDrink: [],
    outdoor: [],
    generalFacilities: [],
    accessibility: [],
    specialLayout: [],
    capacityMaxAdults: '',
    capacityMaxChildren: '',
    capacityMaxChildAgeAllowed: ''
  });
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedImageIndexes, setSelectedImageIndexes] = useState([]);

  useEffect(() => {
    if (!propertyId) {
      console.warn("⛔ Skipping room fetch due to missing propertyId.");
      return;
    }
    fetch(`${apiURL}/api/rooms?propertyId=${propertyId}`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setRooms(data);
        else console.warn("⚠️ rooms response is not an array:", data);
      })
      .catch(err => {
        console.error("❌ Failed to fetch rooms:", err);
      });
  }, [propertyId]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = () => {
    const method = editingRoomId ? 'PUT' : 'POST';
    const url = editingRoomId
      ? `${apiURL}/api/rooms/${editingRoomId}?propertyId=${propertyId}`
      : `${apiURL}/api/rooms`

    const payload = {
      roomType: formData.roomType,
      description: formData.description,
      surface: parseFloat(formData.surface || 0),
      doubleBedCount: parseInt(formData.doubleBedCount || 0),
      singleBedCount: parseInt(formData.singleBedCount || 0),
      viewOptions: formData.viewOptions
        ? formData.viewOptions.split(',').map(v => v.trim()).filter(v => v)
        : [],
      requiresBedChoice: formData.requiresBedChoice,
      fixedBedSetup: formData.requiresBedChoice ? [] : formData.fixedBedSetup,
      bedTypes: formData.requiresBedChoice ? formData.bedTypes : [],
      bathroomFeatures: formData.bathroomFeatures,
      mediaAndTech: formData.mediaAndTech,
      foodAndDrink: formData.foodAndDrink,
      outdoor: formData.outdoor,
      generalFacilities: formData.generalFacilities,
      accessibility: formData.accessibility,
      specialLayout: formData.specialLayout,
      capacity: {
        maxAdults: parseInt(formData.capacityMaxAdults || 0),
        maxChildren: parseInt(formData.capacityMaxChildren || 0),
        maxTotal: parseInt(formData.capacityMaxAdults || 0) + parseInt(formData.capacityMaxChildren || 0),
        maxChildAgeAllowed: parseInt(formData.capacityMaxChildAgeAllowed || 0)
      },
      images: formData.images
        .filter(img => typeof img === 'string' || (img.url && !img.uploading))
        .map(img => typeof img === 'string' ? img : img.url),
    };

    const formPayload = new FormData();
    formPayload.append('roomType', formData.roomType);
    formPayload.append('description', formData.description);
    formPayload.append('surface', parseFloat(formData.surface || 0));
    formPayload.append('doubleBedCount', parseInt(formData.doubleBedCount || 0));
    formPayload.append('singleBedCount', parseInt(formData.singleBedCount || 0));
    formPayload.append('viewOptions', JSON.stringify(
      formData.viewOptions ? formData.viewOptions.split(',').map(v => v.trim()).filter(v => v) : []
    ));
    formPayload.append('requiresBedChoice', formData.requiresBedChoice);
    formPayload.append('fixedBedSetup', JSON.stringify(formData.requiresBedChoice ? [] : formData.fixedBedSetup));
    formPayload.append('bedTypes', JSON.stringify(formData.requiresBedChoice ? formData.bedTypes : []));
    formPayload.append('roomFeatures', JSON.stringify(formData.roomFeatures));
    formPayload.append('bathroomFeatures', JSON.stringify(formData.bathroomFeatures));
    formPayload.append('mediaAndTech', JSON.stringify(formData.mediaAndTech));
    formPayload.append('foodAndDrink', JSON.stringify(formData.foodAndDrink));
    formPayload.append('outdoor', JSON.stringify(formData.outdoor));
    formPayload.append('generalFacilities', JSON.stringify(formData.generalFacilities));
    formPayload.append('accessibility', JSON.stringify(formData.accessibility));
    formPayload.append('specialLayout', JSON.stringify(formData.specialLayout));
    formPayload.append('capacity', JSON.stringify({
      maxAdults: parseInt(formData.capacityMaxAdults || 0),
      maxChildren: parseInt(formData.capacityMaxChildren || 0),
      maxTotal: parseInt(formData.capacityMaxAdults || 0) + parseInt(formData.capacityMaxChildren || 0),
      maxChildAgeAllowed: parseInt(formData.capacityMaxChildAgeAllowed || 0)
    }));
    
    // Append images
    if (formData.images && formData.images.length > 0) {
      formData.images.forEach((file) => {
        if (file instanceof File) {
          formPayload.append('images', file); // Only append actual files
        }
      });
    }
    formPayload.append('images', JSON.stringify(
      formData.images
        .filter(img => typeof img === 'string' || (img.url && !img.uploading))
        .map(img => typeof img === 'string' ? img : img.url)
    ));
    formPayload.append('propertyId', propertyId);
    console.log('📦 Submitting room payload:', {
      roomFeatures: formData.roomFeatures,
      fullForm: [...formPayload.entries()]
    });
    fetch(url, {
      method,
      body: formPayload
    }).then(() => {
      setOpen(false);
      setEditingRoomId(null);
      setFormData({
        roomType: '',
        description: '',
        surface: '',
        bedTypes: [],
        doubleBedCount: '',
        singleBedCount: '',
        viewOptions: '',
        requiresBedChoice: false,
        fixedBedSetup: [],
        roomFeatures: [],
        bathroomFeatures: [],
        mediaAndTech: [],
        foodAndDrink: [],
        outdoor: [],
        generalFacilities: [],
        accessibility: [],
        specialLayout: [],
        capacityMaxAdults: '',
        capacityMaxChildren: '',
        capacityMaxChildAgeAllowed: '',
        images: []
      });
      fetch(`${apiURL}/api/rooms?propertyId=${propertyId}`)
        .then(res => res.json())
        .then(data => setRooms(data));
    });
  };

  const SkeletonBlock = ({ style = {} }) => (
    <motion.div
      initial={{ backgroundPositionX: '100%' }}
      animate={{ backgroundPositionX: '-100%' }}
      transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
      style={{
        width: '100%',
        height: '20px',
        borderRadius: '6px',
        backgroundImage: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
        backgroundSize: '200% 100%',
        backgroundRepeat: 'no-repeat',
        marginBottom: '12px',
        ...style
      }}
    />
  );

  return (
    <Box p={3}>
      <Button variant="contained" onClick={() => setOpen(true)}>+ Add Room</Button>

      <Table sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Room Type</TableCell>
            <TableCell>Surface</TableCell>
            <TableCell>Beds Option</TableCell>
            <TableCell>Views</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rooms.length === 0 ? (
            [...Array(4)].map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={4}>
                  <SkeletonBlock />
                </TableCell>
              </TableRow>
            ))
          ) : (
            rooms.map(room => (
              <TableRow
                key={room._id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={(e) => {
                  const isEditOrDelete = e.target.closest('button');
                  if (!isEditOrDelete) {
                    setSelectedRoom(room);
                  }
                }}
              >
                <TableCell>{room.roomType}</TableCell>
                <TableCell>{room.surface} m²</TableCell>
                <TableCell>
                  {[
                    room.doubleBedCount > 0
                      ? `${room.doubleBedCount} large double bed${room.doubleBedCount > 1 ? 's' : ''}`
                      : '',
                    room.singleBedCount > 0
                      ? `${room.singleBedCount} single bed${room.singleBedCount > 1 ? 's' : ''}`
                      : ''
                  ].filter(Boolean).join(', ')}
                </TableCell>
                <TableCell>{room.viewOptions?.join(', ')}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => {
                    setFormData({
                      ...room,
                      bedTypes: room.bedTypes || [],
                      doubleBedCount: room.doubleBedCount || '',
                      singleBedCount: room.singleBedCount || '',
                      viewOptions: room.viewOptions?.join(', ') || '',
                      requiresBedChoice: room.requiresBedChoice || false,
                      fixedBedSetup: room.fixedBedSetup || [],
                      bathroomFeatures: room.bathroomFeatures || [],
                      mediaAndTech: room.mediaAndTech || [],
                      foodAndDrink: room.foodAndDrink || [],
                      outdoor: room.outdoor || [],
                      generalFacilities: room.generalFacilities || [],
                      accessibility: room.accessibility || [],
                      specialLayout: room.specialLayout || [],
                      images: room.images || [],
                      capacityMaxAdults: room.capacity?.maxAdults || '',
                      capacityMaxChildren: room.capacity?.maxChildren || '',
                      capacityMaxChildAgeAllowed: room.capacity?.maxChildAgeAllowed || ''
                    });
                    setEditingRoomId(room._id);
                    setOpen(true);
                  }}>Edit</Button>
                  <Button size="small" color="error" onClick={() => {
                    fetch(`${apiURL}/api/rooms/${room._id}?propertyId=${propertyId}`, { method: 'DELETE' })
                      .then(() =>
                        fetch(`${apiURL}/api/rooms?propertyId=${propertyId}`)
                          .then(res => res.json())
                          .then(setRooms)
                      );
                  }}>Delete</Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>Add Room Info</DialogTitle>
        <DialogContent>
          <TextField label="Room Type" fullWidth name="roomType" value={formData.roomType} onChange={handleChange} margin="dense" />
          <TextField
            label="Max Adults"
            type="number"
            fullWidth
            name="capacityMaxAdults"
            value={formData.capacityMaxAdults}
            onChange={handleChange}
            margin="dense"
          />
          <TextField
            label="Max Children"
            type="number"
            fullWidth
            name="capacityMaxChildren"
            value={formData.capacityMaxChildren}
            onChange={handleChange}
            margin="dense"
          />
          <TextField
            label="Max Child Age Allowed"
            type="number"
            fullWidth
            name="capacityMaxChildAgeAllowed"
            value={formData.capacityMaxChildAgeAllowed}
            onChange={handleChange}
            margin="dense"
          />
          <TextField label="Description" fullWidth name="description" value={formData.description} onChange={handleChange} margin="dense" multiline />
          <TextField label="Surface (m²)" fullWidth name="surface" value={formData.surface} onChange={handleChange} margin="dense" />
          <FormControl fullWidth margin="dense">
            <InputLabel>Bed Types</InputLabel>
            <Select
              multiple
              value={formData.bedTypes}
              onChange={(e) => setFormData(prev => ({ ...prev, bedTypes: e.target.value }))}
              renderValue={(selected) => selected.join(', ')}
            >
              {['large double bed', 'single beds'].map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={formData.bedTypes.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
        </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.requiresBedChoice}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    requiresBedChoice: e.target.checked
                  }))
                }
              />
            }
            label="Let guest choose bed type?"
          />    
          {!formData.requiresBedChoice && (
            <FormControl fullWidth margin="dense">
            <InputLabel>Fixed Bed Setup</InputLabel>
            <Select
              multiple
              value={formData.fixedBedSetup}
              onChange={(e) => setFormData((prev) => ({ ...prev, fixedBedSetup: e.target.value }))}
              renderValue={(selected) => selected.join(', ')}
            >
              {['large double bed', 'single beds'].map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={formData.fixedBedSetup.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          )}
          <TextField
            label="Large Double Bed Count"
            type="number"
            fullWidth
            name="doubleBedCount"
            value={formData.doubleBedCount}
            onChange={handleChange}
            margin="dense"
          />
          <TextField
            label="Single Bed Count"
            type="number"
            fullWidth
            name="singleBedCount"
            value={formData.singleBedCount}
            onChange={handleChange}
            margin="dense"
          />    
          <TextField label="View Options (comma-separated)" fullWidth name="viewOptions" value={formData.viewOptions} onChange={handleChange} margin="dense" />
          <FormControl fullWidth margin="dense">
            <InputLabel>Room Features</InputLabel>
            <Select
              multiple
              value={formData.roomFeatures}
              onChange={(e) => setFormData(prev => ({ ...prev, roomFeatures: e.target.value }))}
              renderValue={(selected) => selected.join(', ')}
            >
              {[
                'Private Suite','Pool Access','Balcony', 'Pool View','Garden view', 'Landmark view', 'Inner courtyard view', 'Air conditioning',
                'Spa bath', 'Patio', 'Ensuite bathroom', 'Flat-screen TV', 'Soundproofing',
                'Terrace', 'Minibar', 'Free WiFi', 'Hot tub', 'Free toiletries', 'Bathrobe',
                'Safety deposit box', 'Additional bathroom', 'Bidet', 'Toilet', 'Sofa',
                'Bath or shower', 'Towels', 'Linen', 'Socket near the bed', 'Tile/marble floor',
                'Desk', 'Seating Area', 'TV', 'Slippers', 'Refrigerator', 'Telephone',
                'Tea/Coffee maker', 'Hairdryer', 'Fan', 'Additional toilet', 'Extra long beds (> 2 metres)',
                'Towels/sheets (extra fee)', 'Carpeted', 'Electric kettle', 'Outdoor furniture',
                'Outdoor dining area', 'Wake-up service', 'Wardrobe or closet', 'Dining area',
                'Dining table', 'Upper floors accessible by stairs only', 'Clothes rack', 'Toilet paper',
                'Sofa bed'
              ].map(option => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={formData.roomFeatures.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Bathroom Features</InputLabel>
            <Select
                multiple
                value={formData.bathroomFeatures}
                onChange={(e) => setFormData(prev => ({ ...prev, bathroomFeatures: e.target.value }))}
                renderValue={(selected) => selected.join(', ')}
            >
                {[
                'Hot tub', 'Free toiletries', 'Bathrobe',
                'Additional bathroom', 'Bidet', 'Toilet', 'Bath or shower',
                'Towels', 'Linen', 'Toilet paper'
                ].map(option => (
                <MenuItem key={option} value={option}>
                    <Checkbox checked={formData.bathroomFeatures.indexOf(option) > -1} />
                    <ListItemText primary={option} />
                </MenuItem>
                ))}
            </Select>
            </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Media & Technology</InputLabel>
            <Select
              multiple
              value={formData.mediaAndTech}
              onChange={(e) => setFormData(prev => ({ ...prev, mediaAndTech: e.target.value }))}
              renderValue={(selected) => selected.join(', ')}
            >
              {[
                'Flat-screen TV', 'TV', 'Music TV channels', 'Satellite/cable TV',
                'Computer', 'Printer', 'Telephone', 'High speed internet'
              ].map(option => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={formData.mediaAndTech.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Food & Drink</InputLabel>
            <Select
              multiple
              value={formData.foodAndDrink}
              onChange={(e) => setFormData(prev => ({ ...prev, foodAndDrink: e.target.value }))}
              renderValue={(selected) => selected.join(', ')}
            >
              {[
                'Minibar', 'Refrigerator', 'Tea/Coffee maker', 'Electric kettle',
                'Dining area', 'Dining table', 'Breakfast in bed', 'Outdoor dining area'
              ].map(option => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={formData.foodAndDrink.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Outdoor</InputLabel>
            <Select
              multiple
              value={formData.outdoor}
              onChange={(e) => setFormData(prev => ({ ...prev, outdoor: e.target.value }))}
              renderValue={(selected) => selected.join(', ')}
            >
              {[
                'Private Pool','Terrace', 'Patio', 'Balcony', 'Outdoor furniture', 'Outdoor dining area', 'Garden view'
              ].map(option => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={formData.outdoor.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>General Facilities</InputLabel>
            <Select
              multiple
              value={formData.generalFacilities}
              onChange={(e) => setFormData(prev => ({ ...prev, generalFacilities: e.target.value }))}
              renderValue={(selected) => selected.join(', ')}
            >
              {[
                'Soundproofing', 'Carpeted', 'Fan', 'Wardrobe or closet', 'Desk', 'Seating Area',
                'Sofa', 'Clothes rack', 'Wake-up service', 'Socket near the bed'
              ].map(option => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={formData.generalFacilities.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Accessibility and Security</InputLabel>
            <Select
              multiple
              value={formData.accessibility}
              onChange={(e) => setFormData(prev => ({ ...prev, accessibility: e.target.value }))}
              renderValue={(selected) => selected.join(', ')}
            >
              {[
                 'Safety deposit box','Upper floors accessible by stairs only', 'Visual alarm for hearing impaired',
                'Audible smoke alarms', 'Emergency info in rooms', 'Phone light for hearing impaired',
                'Knock light for hearing impaired'
              ].map(option => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={formData.accessibility.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Special Layout</InputLabel>
            <Select
                multiple
                value={formData.specialLayout}
                onChange={(e) => setFormData(prev => ({ ...prev, specialLayout: e.target.value }))}
                renderValue={(selected) => selected.join(', ')}
            >
                {[
                'Private suite',
                'Private pool',
                'Entire unit located on ground floor',
                'Entire unit wheelchair accessible'
                ].map(option => (
                <MenuItem key={option} value={option}>
                    <Checkbox checked={formData.specialLayout.indexOf(option) > -1} />
                    <ListItemText primary={option} />
                </MenuItem>
                ))}
            </Select>
            </FormControl>
            <Box mt={2}>
              <label><strong>Upload Room Images</strong></label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={async (e) => {
                  const files = Array.from(e.target.files);
                  for (const file of files) {
                    const tempUrl = URL.createObjectURL(file);
                    // Show loading placeholder
                    setFormData(prev => ({
                      ...prev,
                      images: [...(prev.images || []), { url: tempUrl, uploading: true }]
                    }));
                    const formData = new FormData();
                    formData.append('image', file);
                    try {
                      const res = await fetch(`${apiURL}/api/upload`, {
                        method: 'POST',
                        body: formData
                      });
                      const data = await res.json();
                      setFormData(prev => ({
                        ...prev,
                        images: prev.images.map(img =>
                          img.url === tempUrl ? { url: data.imageUrl, uploading: false } : img
                        )
                      }));
                    } catch (err) {
                      console.error("Image upload failed:", err);
                      setFormData(prev => ({
                        ...prev,
                        images: prev.images.filter(img => img.url !== tempUrl)
                      }));
                    }
                  }
                }}
              />
            </Box>
            {formData.images?.length > 0 && (
              <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                {formData.images.map((file, idx) => {
                  const isUploading = file.uploading;
                  const imageUrl = typeof file === 'string' ? file : file.url || URL.createObjectURL(file);
                  return (
                    <Box key={idx} sx={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={imageUrl}
                        alt={`Image ${idx}`}
                        onClick={() => {
                          if (isUploading) return;
                          setSelectedImageIndexes(prev =>
                            prev.includes(idx)
                              ? prev.filter(i => i !== idx)
                              : [...prev, idx]
                          );
                        }}
                        style={{
                          width: 120,
                          height: 80,
                          borderRadius: 4,
                          cursor: 'pointer',
                          opacity: isUploading ? 0.5 : 1,
                          outline: selectedImageIndexes.includes(idx) ? '2px solid #007aff' : 'none'
                        }}
                      />
                      {isUploading && (
                        <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          background: 'rgba(255,255,255,0.8)',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          Uploading...
                        </Box>
                      )}
                      {!isUploading && (
                        <Box
                          onClick={async () => {
                            const imageToDelete = formData.images[idx];
                            const updatedImages = formData.images.filter((_, i) => i !== idx);
 
                            setFormData((prev) => ({
                              ...prev,
                              images: updatedImages
                            }));
 
                            if (editingRoomId && typeof imageToDelete === 'string') {
                              setRooms((prevRooms) =>
                                prevRooms.map((room) =>
                                  room._id === editingRoomId
                                    ? {
                                        ...room,
                                        images: room.images.filter((img) => img !== imageToDelete)
                                      }
                                    : room
                                )
                              );
                              setSelectedRoom(prev => {
                                if (!prev || !prev.images) return null;
                                return {
                                  ...prev,
                                  images: prev.images.filter((img) => img !== imageToDelete)
                                };
                              });
                            }
 
                            if (typeof imageToDelete === 'string') {
                              await fetch(`${apiURL}/api/rooms/image?filename=${encodeURIComponent(imageToDelete)}`, {
                                method: 'DELETE'
                              });
                            }
                          }}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            minWidth: 'auto',
                            padding: '4px',
                            lineHeight: 1,
                            background: 'white',
                            borderRadius: '50%',
                            color: 'red',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          ✖
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
                (!formData.requiresBedChoice && formData.fixedBedSetup.length === 0)
            }
            >
            Save
        </Button>
    
        </DialogActions>
      </Dialog>

      <Dialog open={!!selectedRoom} onClose={() => setSelectedRoom(null)} fullWidth maxWidth="md">
        <DialogTitle>{selectedRoom?.roomType} - Details</DialogTitle>
        <DialogContent dividers>
          {selectedRoom?.images?.length > 0 && (
            <Box mb={3} display="flex" flexWrap="wrap" gap={2}>
              {selectedRoom.images.map((img, index) => {
                const src = typeof img === 'string'
                  ? img
                  : img?.url
                    ? img.url
                    : img?.filename
                      ? `${apiURL}/uploads/${img.filename}`
                      : '';
                
                return (
                  <img
                    key={index}
                    src={src}
                    alt={`Room image ${index}`}
                    width={120}
                    height={90}
                    style={{ objectFit: 'cover', borderRadius: 6 }}
                  />
                );
              })}
            </Box>
          )}
          <Box sx={{ p: 1 }}>
            <Box mb={2}><strong>Room Type:</strong> {selectedRoom?.roomType}</Box>
            <Box mb={2}><strong>Surface:</strong> {selectedRoom?.surface} m²</Box>
            {selectedRoom?.capacity?.maxAdults !== undefined && (
              <Box mb={2}><strong>Max Adults:</strong> {selectedRoom.capacity.maxAdults}</Box>
            )}
            {selectedRoom?.capacity?.maxChildren !== undefined && (
              <Box mb={2}><strong>Max Children:</strong> {selectedRoom.capacity.maxChildren}</Box>
            )}
            {selectedRoom?.capacity?.maxChildAgeAllowed !== undefined && (
              <Box mb={2}><strong>Max Child Age Allowed:</strong> {selectedRoom.capacity.maxChildAgeAllowed} years old</Box>
            )}
            <Box mb={2}>
              <strong>Beds:</strong>{' '}
              {selectedRoom?.requiresBedChoice
                ? selectedRoom?.bedTypes?.join(', ')
                : selectedRoom?.fixedBedSetup?.join(', ')}{' '}
              ({[selectedRoom?.doubleBedCount > 0
                ? `${selectedRoom.doubleBedCount} large double bed${selectedRoom.doubleBedCount > 1 ? 's' : ''}`
                : '',
                selectedRoom?.singleBedCount > 0
                ? `${selectedRoom.singleBedCount} single bed${selectedRoom.singleBedCount > 1 ? 's' : ''}`
                : '']
                .filter(Boolean)
                .join(', ')
              })
            </Box>
            <Box mb={2}><strong>Views:</strong> {selectedRoom?.viewOptions?.join(', ')}</Box>
            <Box mb={2}><strong>Description:</strong> {selectedRoom?.description}</Box>
            {[
              ['Room Features', selectedRoom?.roomFeatures],
              ['Bathroom Features', selectedRoom?.bathroomFeatures],
              ['Media & Technology', selectedRoom?.mediaAndTech],
              ['Food & Drink', selectedRoom?.foodAndDrink],
              ['Outdoor', selectedRoom?.outdoor],
              ['General Facilities', selectedRoom?.generalFacilities],
              ['Accessibility', selectedRoom?.accessibility],
              ['Special Layout', selectedRoom?.specialLayout]
            ].map(([label, list]) => (
              list?.length > 0 && (
                <Box key={label} mb={1}>
                  <strong>{label}:</strong> {list.join(', ')}
                </Box>
              )
          ))}
          
          <Box mb={2}><strong>Urgent Availability:</strong> {selectedRoom?.urgentAvailability ? 'Yes' : 'No'}</Box>
          <Box mb={2}><strong>Currently Available:</strong> {selectedRoom?.isAvailable ? 'Yes' : 'No'}</Box>
          
          {selectedRoom?.images?.length > 0 && (
            <Box mb={2}>
              <strong>Images:</strong> {selectedRoom.images.length} image{selectedRoom.images.length > 1 ? 's' : ''}
            </Box>
          )}
          
          <Box mb={2}><strong>Created At:</strong> {new Date(selectedRoom?.createdAt).toLocaleString()}</Box>
          <Box mb={2}><strong>Last Updated:</strong> {new Date(selectedRoom?.updatedAt).toLocaleString()}</Box>
        </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRoom(null)}>Close</Button>

        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoomManager;